import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar, faBolt, faWallet, faLaptop, faGlobe, faBriefcase, faUsers, faPlane
} from "@fortawesome/free-solid-svg-icons";
import NavBar from "../Navigation/NavBar";
import Footer from "../Footer/Footer";
import Services1 from "./IMG/Services1.png";
import Services2 from "./IMG/Services2.png";
import Services3 from "./IMG/Services3.png";
import Services4 from "./IMG/Services4.png";
import Services5 from "./IMG/Services5.png";
import Services6 from "./IMG/Services6.png";
import Services7 from "./IMG/Services7.png";
import Services8 from "./IMG/Services8.png";
import "./Services.css";

const services = [
  { path: "/PermanentStaffing", title: "Permanent Staffing", image: Services1, icon: faStar, desc: "Define the role, agree on selection criteria and coordinate the process for a permanent hire." },
  { path: "/TemporaryStaffing", title: "Temporary Staffing", image: Services2, icon: faBolt, desc: "Plan assignment dates, work conditions, supervision and handover before a temporary placement begins." },
  { path: "/PayRoll", title: "Payroll Management", image: Services3, icon: faWallet, desc: "Prepare reliable payroll inputs, approval steps, records and employee query handling." },
  { path: "/DedicatedIt", title: "Dedicated IT Resources", image: Services4, icon: faLaptop, desc: "Specify technical outcomes, required skills, working arrangements and access responsibilities." },
  { path: "/International", title: "International Recruitment", image: Services5, icon: faGlobe, desc: "Coordinate role requirements and candidate communication across borders, with destination rules verified separately." },
  { path: "/Executive", title: "Executive Search", image: Services6, icon: faBriefcase, desc: "Set a clear leadership mandate, evaluate evidence and protect confidential candidate information." },
  { path: "/BulkHiring", title: "Bulk Hiring", image: Services7, icon: faUsers, desc: "Plan high-volume hiring batches, consistent assessment, interview schedules and joining logistics." },
  { path: "/VisaAssistance", title: "Visa Assistance", image: Services8, icon: faPlane, desc: "Prepare questions and document checklists using current official guidance; authorities decide applications." }
];

const preparation = [
  { title: "Role and outcomes", text: "Share the job duties, location, employment type, essential skills and the outcomes expected in the first months." },
  { title: "Selection plan", text: "Agree who reviews applications, how interviews will be structured and who has authority to make the final decision." },
  { title: "Terms and timing", text: "Clarify the compensation range, working hours, start-date window and any checks that candidates should expect." },
  { title: "Candidate communication", text: "Choose a contact person, keep updates consistent and collect only information needed for the current recruitment stage." }
];

function MainServices() {
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "smooth" }); }, []);
  return (
    <>
      <Helmet>
        <meta name="author" content="Quarec Resources" />
      </Helmet>
      <NavBar />
      <div className="Services-Hero">
        <div className="Services-Hero-Content">
          <h1 className="Services-Hero-Title">Recruitment and Staffing Services</h1>
          <p className="Services-Hero-Subtitle">Understand each service, prepare the right information and agree on the process before recruitment begins.</p>
        </div>
      </div>
      <section className="gallery-wrapper">
        <div className="gallery-container">
          <h2 className="gallery-section-title">Explore our services</h2>
          <p style={{maxWidth:900,margin:"-24px auto 38px",textAlign:"center",lineHeight:1.8,color:"#4b5563"}}>
            Quarec Resources supports recruitment and HR engagements for employers and jobseekers. The scope, deliverables and timing are agreed for each engagement; a placement, job offer or immigration outcome is not guaranteed.
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <article key={service.path} className="service-card" style={{animationDelay:`${index * 0.06}s`}}>
                <div className="service-card-image-wrapper">
                  <img src={service.image} alt={`${service.title} service`} className="service-card-image" loading="lazy" />
                  <div className="service-card-overlay"></div>
                  <div className="service-card-badge"><FontAwesomeIcon icon={service.icon} /></div>
                </div>
                <div className="service-card-content">
                  <h3 className="service-card-title">{service.title}</h3>
                  <p className="service-card-desc">{service.desc}</p>
                  <div className="service-card-footer"><a className="service-card-btn" href={service.path}>Read service guide <span className="arrow">→</span></a></div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section style={{padding:"64px 22px",background:"#fff"}}>
        <div style={{maxWidth:1180,margin:"0 auto"}}>
          <h2 style={{fontSize:"clamp(26px,3vw,36px)",color:"#1e4a4d",marginBottom:12}}>What employers should prepare</h2>
          <p style={{maxWidth:850,lineHeight:1.8,color:"#4b5563"}}>A useful recruitment brief helps the employer and recruitment team work from the same information. Before a search begins, review these four areas.</p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:18,marginTop:26}}>
            {preparation.map((item) => <article key={item.title} style={{padding:22,border:"1px solid #dbe5e5",borderRadius:14,background:"#f8fbfb"}}><h3 style={{fontSize:19,color:"#1e4a4d",marginTop:0}}>{item.title}</h3><p style={{lineHeight:1.75,color:"#4b5563",marginBottom:0}}>{item.text}</p></article>)}
          </div>
        </div>
      </section>
      <section style={{padding:"58px 22px",background:"#f3f8f8"}}>
        <div style={{maxWidth:1180,margin:"0 auto"}}>
          <h2 style={{fontSize:"clamp(26px,3vw,36px)",color:"#1e4a4d",marginBottom:24}}>A clear recruitment process</h2>
          <ol style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:18,padding:0,listStyle:"none"}}>
            {[
              ["1. Brief", "Agree the role, required skills, location, terms and decision owner."],
              ["2. Source and review", "Review candidates against the agreed job-related criteria and record the evidence."],
              ["3. Interview and decide", "Use a consistent interview plan and communicate next steps to candidates."],
              ["4. Offer and join", "Confirm written terms, required checks and the joining plan before the start date."]
            ].map(([title,text]) => <li key={title} style={{background:"#fff",padding:22,borderRadius:14,border:"1px solid #dbe5e5"}}><h3 style={{marginTop:0,color:"#1e4a4d"}}>{title}</h3><p style={{lineHeight:1.75,color:"#4b5563",marginBottom:0}}>{text}</p></li>)}
          </ol>
          <h2 style={{fontSize:24,color:"#1e4a4d",marginTop:34}}>Common questions</h2>
          <h3 style={{fontSize:18}}>Can Quarec guarantee a placement or job offer?</h3>
          <p style={{lineHeight:1.75,color:"#4b5563"}}>No. Hiring decisions depend on the employer’s requirements and selection process, and candidates decide whether to accept an offer.</p>
          <h3 style={{fontSize:18}}>Does visa assistance guarantee approval?</h3>
          <p style={{lineHeight:1.75,color:"#4b5563"}}>No. Visa and work-authorisation decisions and processing times are determined by the relevant authorities. Check current requirements through official channels.</p>
          <p style={{marginTop:24}}><a href="/Resources/Hiring-Brief" style={{color:"#176e6d",fontWeight:700}}>Use the free Hiring Brief Builder →</a> &nbsp; <a href="/ContactUs" style={{color:"#176e6d",fontWeight:700}}>Contact Quarec Resources →</a></p>
        </div>
      </section>
      <Footer />
    </>
  );
}
export default MainServices;
